import { TFunction } from 'i18next';
import { MedtechUserV2 } from '../user';

export type ActivityLogAction =
  | ActivityLogModificationAction
  | 'approved'
  | 'declined'
  | 'reviewed'
  | 'editing_completed'
  | 'changed_status';

export type ActivityLogModificationAction =
  | 'modified_quality_approvers'
  | 'modified_other_approvers'
  | 'modified_reviewers'
  | 'modified_editors'
  | 'modified_doc_title'
  | 'modified_doc_code'
  | 'modified_doc_owner'
  | 'modified_doc_review_period'
  | 'modified_doc_training_required'
  | 'modified_doc_effective_on_approval'
  | 'modified_doc_format'
  | 'modified_doc_tags'
  | 'modified_file_download_permission_group'
  | 'upgraded_document_template'
  | 'created_new_draft'
  | 'edited_sections'
  | 'modified_external_file_upload'
  | 'modified_upload_file';

export type ActivityLogEntityType = 'document' | 'change_control';

export type ActivityLog = {
  entity_id: string;
  entity_type: ActivityLogEntityType;
  action: ActivityLogAction;
  timestamp: number;
  actor: MedtechUserV2 | null;
  data: any;
  key?: number;
};

type ActivityLogActionVerb =
  | 'modified'
  | 'approved'
  | 'declined'
  | 'upgraded'
  | 'reviewed'
  | 'created'
  | 'edited'
  | 'updated';

type ActivityLogActionEntity =
  | 'Quality approvers'
  | 'Other approvers'
  | 'Reviewers'
  | 'Editors'
  | 'Owner'
  | 'Title'
  | 'ID'
  | 'Review period'
  | 'Retraining required'
  | 'Effective on approval'
  | 'Format'
  | 'Tags'
  | 'Download permissions'
  | 'Document template'
  | 'New draft'
  | 'Section'
  | 'External file'
  | 'Upload file';

type ActivityLogActionToEntityAndVerb = {
  [key in ActivityLogModificationAction]: {
    verb: ActivityLogActionVerb;
    entity: ActivityLogActionEntity;
  };
};

export const activityLogDetailMap: ActivityLogActionToEntityAndVerb = {
  modified_quality_approvers: { verb: 'modified', entity: 'Quality approvers' },
  modified_other_approvers: { verb: 'modified', entity: 'Other approvers' },
  modified_reviewers: { verb: 'modified', entity: 'Reviewers' },
  modified_editors: { verb: 'modified', entity: 'Editors' },
  modified_doc_owner: { verb: 'modified', entity: 'Owner' },
  modified_doc_title: { verb: 'modified', entity: 'Title' },
  modified_doc_code: { verb: 'modified', entity: 'ID' },
  modified_doc_review_period: { verb: 'modified', entity: 'Review period' },
  modified_doc_training_required: {
    verb: 'modified',
    entity: 'Retraining required',
  },
  modified_doc_effective_on_approval: {
    verb: 'modified',
    entity: 'Effective on approval',
  },
  modified_doc_format: {
    verb: 'modified',
    entity: 'Format',
  },
  modified_file_download_permission_group: {
    verb: 'modified',
    entity: 'Download permissions',
  },
  modified_doc_tags: {
    verb: 'modified',
    entity: 'Tags',
  },
  upgraded_document_template: {
    verb: 'upgraded',
    entity: 'Document template',
  },
  created_new_draft: {
    verb: 'created',
    entity: 'New draft',
  },
  edited_sections: {
    verb: 'edited',
    entity: 'Section',
  },
  modified_external_file_upload: {
    verb: 'updated',
    entity: 'External file',
  },
  modified_upload_file: {
    verb: 'modified',
    entity: 'Upload file',
  },
};

export const getActivityLogDetailMap = (
  t: TFunction,
): ActivityLogActionToEntityAndVerb => ({
  modified_quality_approvers: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.qualityApprovers'),
  },
  modified_other_approvers: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.otherApprovers'),
  },
  modified_reviewers: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.reviewers'),
  },
  modified_editors: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.editors'),
  },
  modified_doc_owner: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.owner'),
  },
  modified_doc_title: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.title'),
  },
  modified_doc_code: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.id'),
  },
  modified_doc_review_period: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.reviewPeriod'),
  },
  modified_doc_training_required: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.retrainingRequired'),
  },
  modified_doc_effective_on_approval: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.effectiveOnApproval'),
  },
  modified_doc_format: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.format'),
  },
  modified_file_download_permission_group: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.downloadPermissions'),
  },
  modified_doc_tags: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.tags'),
  },
  upgraded_document_template: {
    verb: t('activityActions.upgraded'),
    entity: t('activityActions.documentTemplate'),
  },
  created_new_draft: {
    verb: t('activityActions.created'),
    entity: t('activityActions.newDraft'),
  },
  edited_sections: {
    verb: t('activityActions.edited'),
    entity: t('activityActions.section'),
  },
  modified_external_file_upload: {
    verb: t('activityActions.updated'),
    entity: t('activityActions.externalFile'),
  },
  modified_upload_file: {
    verb: t('activityActions.modified'),
    entity: t('activityActions.uploadFile'),
  },
});
